/**
 * Demo access-control matrix
 * --------------------------------------------------------------------------
 * Maps each demo persona (from the Role Switcher / DemoRoleContext) to the set
 * of Drift & Accumulation routes it is allowed to see and reach. Consumed by:
 *   - Sidebar.jsx   → hides nav items the persona can't access
 *   - DemoAccessGate → blocks direct navigation to a restricted route
 *
 * Derived from Tim's mindmap (Access Levels branch).
 *
 * IMPORTANT: this is *UI-level* gating to make the demo's role experience
 * authentic. It is NOT a security boundary — true enforcement lives in the
 * Firestore security rules (a separate, post-demo hardening track). Don't
 * represent this to a client's IT/security team as the access-control
 * mechanism; it's the visible layer on top of it.
 */

const ROOT = 'drew'; // P6-14: DREW routes live at /drew/... (legacy /tools/drift-accumulation/... redirects)
const T = (suffix) => (suffix ? `${ROOT}/${suffix}` : ROOT);

export const FULL_ACCESS = '*';

/**
 * Allowed route bases per persona id. A persona may reach a route if it exactly
 * matches an allowed base, or is a child of one (so `case-studies` also grants
 * `case-studies/:id`). The dashboard root is exact-match only (it would
 * otherwise prefix-match every route).
 */
export const PERSONA_ACCESS = {
  // Administrator + PPI Tech Support — full access.
  admin: FULL_ACCESS,
  ppi: FULL_ACCESS,

  // Executive (COO) — high-level, read-oriented: enterprise rollups + IA outputs.
  executive: [
    T(),
    T('case-studies'),
    T('surveys'),
    T('reports'),
    T('role/executive'),
    T('rollup/trl-entry'), // P2-07: everyone up to the CEO can log conditions (C0820)
    T('rollup/site'),
    T('rollup/regional'),
    T('rollup/enterprise'),
    T('ia/comparisons'),
    // 'ia/barometer' moved to /hugo/barometer (P6-15; HUGO flag)
    T('ia/health-messages'),
    T('ia/reports-on-demand'),
  ],

  // Site Manager — site-scoped: their site's dashboards, surveys, comparisons.
  site_manager: [
    T(),
    T('case-studies'),
    T('surveys'),
    T('reports'),
    T('rollup/trl-entry'), // P2-07
    T('rollup/review-queue'), // L1 for Department Head entrants (C45 routing)
    T('rollup/severity-calibration'), // L2 for FLS entrants
    T('rollup/site'),
    T('ia/comparisons'),
    // 'ia/barometer' moved to /hugo/barometer (P6-15; HUGO flag)
    T('ia/health-messages'),
    T('ia/reports-on-demand'),
  ],

  // Department Head — verify/adjust NWT severity, assign priorities, dept→site rollups.
  dh: [
    T(),
    T('case-studies'),
    T('surveys'),
    T('reports'),
    T('role/dh'),
    T('rollup/trl-entry'), // P2-07
    T('rollup/review-queue'), // L1 for FLS entrants (C45 routing)
    T('rollup/severity-calibration'),
    T('rollup/site'),
    T('rollup/regional'),
    T('ia/comparisons'),
    // 'ia/barometer' moved to /hugo/barometer (P6-15; HUGO flag)
    T('ia/health-messages'),
    T('ia/reports-on-demand'),
  ],

  // Front Line Supervisor — NWT view, review/approve ITM input, log on team's behalf.
  fls: [
    T(),
    T('case-studies'),
    T('surveys'),
    T('role/fls'),
    T('rollup/trl-entry'),
    T('rollup/review-queue'),
    T('rollup/site'),
    // 'ia/barometer' moved to /hugo/barometer (P6-15; HUGO flag)
    T('ia/health-messages'),
  ],

  // Individual Contributor — log OE Conditions; view-only NWT + Site dashboards.
  itm: [
    T(),
    T('case-studies'),
    T('surveys'),
    T('role/itm'),
    T('rollup/trl-entry'),
    T('rollup/site'),
    T('ia/health-messages'),
  ],

  // Org IT — data integration surfaces: IT portal, TRL system, document intake.
  org_it: [
    T(),
    T('documents'),
    T('settings'),
    T('role/org-it'),
    T('ia/it-portal'),
    T('ia/trl-system'),
  ],
};

function normalize(path) {
  return String(path || '').replace(/^\/+/, '').replace(/\/+$/, '');
}

/**
 * Can the given persona access the given tool route?
 * @param {string} personaId - DemoRoleContext persona id (e.g. 'itm')
 * @param {string} path - route path, with or without leading slash
 * @returns {boolean}
 */
export function canAccessToolPath(personaId, path) {
  const allowed = PERSONA_ACCESS[personaId];
  // Unknown / unset persona → don't block (safe default; e.g. before context loads)
  if (!allowed) return true;
  if (allowed === FULL_ACCESS) return true;

  const p = normalize(path);
  // Anything outside the D&A tool isn't governed by this matrix.
  if (!p.startsWith(ROOT)) return true;

  return allowed.some((base) => {
    const b = normalize(base);
    if (b === ROOT) return p === ROOT; // dashboard root: exact only
    return p === b || p.startsWith(`${b}/`);
  });
}
